<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Loading state -->
    <empty-state v-if="isLoading" :title="$t('loading')" :loading="true" />

    <!-- Empty state: No data -->
    <empty-state
      v-else-if="!orders.length"
      icon="mdi-receipt-text-outline"
      :title="$t('no_data')"
      :subtitle="$t('no_orders_for_selected_period')"
    />

    <!-- Desktop View -->
    <v-data-table
      v-else-if="!device.isMobile"
      :headers="headers"
      :items="customSearch(orders, headers, search)"
      class="ordera-box-shadow striped-table"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
    >
      <template v-slot:item.date="{ item }">
        {{ formatDate(item.date) }}
      </template>
      <template v-slot:item.restaurant_name="{ item }">
        {{ item.restaurant_name }}
      </template>
      <template v-slot:item.total="{ item }">
        {{ formatCurrency(item.total) }}
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="customSearch(orders, headers, search)"
      item-key="id"
      :fields-per-row="null"
      :bold-fields="['date']"
      :show-row-number="false"
      class="px-3"
    >
      <template v-slot:item.date="{ item }">
        {{ formatDate(item.date) }}
      </template>
      <template v-slot:item.restaurant_name="{ item }">
        {{ item.restaurant_name }}
      </template>
      <template v-slot:item.total="{ item }">
        {{ formatCurrency(item.total) }}
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import { generateMockCustomerOrders } from "@/data/customerOrders";

export default {
  name: "CustomerReportTabOrders",
  mixins: [hasSnackbar],
  components: {
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      isLoading: false,
      orders: [],
    };
  },
  computed: {
    dates() {
      return this.$store.state.report?.filters?.dates || { from: null, to: null };
    },
    selectedRestaurants() {
      return this.$store.state.report?.selectedRestaurants || [];
    },
    selectedShifts() {
      return this.$store.state.report?.filters?.selectedShifts || [];
    },
    search() {
      return this.$store.state.report?.filters?.search || "";
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("restaurant"), value: "restaurant_name" },
        { text: this.$t("meal"), value: "meal_name" },
        { text: this.$t("quantity"), value: "quantity", align: "center" },
        { text: this.$t("price"), value: "price", align: "right" },
        { text: this.$t("total"), value: "total", align: "right" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("date"), value: "date", cols: 6 },
        { text: this.$t("restaurant"), value: "restaurant_name", cols: 6 },
        { text: this.$t("meal"), value: "meal_name", cols: 12 },
        { text: this.$t("quantity"), value: "quantity", cols: 3 },
        { text: this.$t("price"), value: "price", cols: 4 },
        { text: this.$t("total"), value: "total", cols: 5 },
      ];
    },
  },
  watch: {
    dates: {
      handler() {
        this.fetchOrders();
      },
      deep: true,
    },
    selectedRestaurants: {
      handler() {
        this.fetchOrders();
      },
      deep: true,
    },
    selectedShifts: {
      handler() {
        this.fetchOrders();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchOrders();
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      return this.localeDate(date);
    },
    formatCurrency(amount) {
      if (!amount) return "0.00";
      return parseFloat(amount).toFixed(2);
    },
    async fetchOrders() {
      this.isLoading = true;
      try {
        // TODO: Implement API call
        // const response = await reportService.getCustomerOrders({
        //   date_from: this.dates.from,
        //   date_to: this.dates.to,
        //   shifts: this.selectedShifts
        // });

        // Using mock data for now - filter by date range
        await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API delay

        const { from, to } = this.dates;
        const fromDate = from ? new Date(from) : null;
        const toDate = to ? new Date(to) : null;

        // Generate mock orders with restaurants from store
        const restaurants = this.$store.state.company?.restaurants || [];
        let filteredOrders = generateMockCustomerOrders(restaurants);

        // Filter by date range
        if (fromDate && toDate) {
          filteredOrders = filteredOrders.filter((order) => {
            const orderDate = new Date(order.date);
            return orderDate >= fromDate && orderDate <= toDate;
          });
        }

        // Filter by selected restaurants
        if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
          filteredOrders = filteredOrders.filter((order) =>
            this.selectedRestaurants.includes(order.restaurant_id)
          );
        }

        this.orders = filteredOrders.map((order, index) => ({
          ...order,
          num: index + 1,
        }));
      } catch (error) {
        console.error("Error fetching orders:", error);
        this.showErrorSnackbar(error, this.$t("error_loading_orders"));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped></style>
